import * as THREE from 'three';
import { DigitalObject } from '../../rendering/binary/DigitalObject.js';

/**
 * Matrix patrol craft — instanced 0/1 hull built from the same DigitalObject pipeline as NPCs.
 */
export class FlyingShip {
  /**
   * @param {{
   *   material: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   seed: number;
   *   cellIdBase: number;
   *   config: import('../../config.js').CONFIG['flyingShips'];
   * }} options
   */
  constructor({ material, seed, cellIdBase, config }) {
    this.config = config;
    this.root = new THREE.Group();
    this.root.name = 'FlyingShip';

    const parts = [
      {
        shape: 'box',
        size: [4.6, 1.05, 1.2],
        position: [0, 0, 0],
        cellsPerMeter: 4,
        name: 'Hull',
      },
      {
        shape: 'facade',
        size: [1.55, 0.82, 0.95],
        position: [0.55, 0.98, 0],
        cellsPerMeter: 5,
        name: 'Cabin',
      },
      {
        shape: 'box',
        size: [5.4, 0.16, 2.35],
        position: [0, -0.12, 0],
        cellsPerMeter: 3,
        name: 'Wings',
      },
      {
        shape: 'box',
        size: [1.15, 0.48, 0.75],
        position: [2.65, 0.08, 0],
        cellsPerMeter: 4,
        name: 'Nose',
      },
      {
        shape: 'box',
        size: [0.85, 0.55, 0.65],
        position: [-2.35, 0.22, 0],
        cellsPerMeter: 4,
        name: 'Tail',
      },
    ];

    let cellOffset = cellIdBase;
    let maxColumns = 1;
    const faceSeed = seed;
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      const body = new DigitalObject({
        material,
        shape: part.shape,
        size: part.size,
        position: part.position,
        cellsPerMeter: part.cellsPerMeter,
        seed: faceSeed + i * 41,
        cellIdBase: cellOffset,
        name: part.name,
      });
      cellOffset += body.cellsUsed;
      for (const surface of body.surfaces) {
        maxColumns = Math.max(maxColumns, surface.columns);
      }
      this.root.add(body.group);
    }
    this.cellsUsed = cellOffset - cellIdBase;
    this.maxColumns = maxColumns;

    const hash = (n) => ((seed * 1103515245 + n * 12345) >>> 0) / 4294967296;
    const t = (min, max, n) => min + (max - min) * hash(n);

    this.orbitRadius = t(config.orbitRadiusMin, config.orbitRadiusMax, 1);
    this.orbitSpeed = t(config.speedMin, config.speedMax, 2);
    this.orbitPhase = hash(3) * Math.PI * 2;
    this.altitude = t(config.altitudeMin, config.altitudeMax, 4);
    this.bankPhase = hash(5) * Math.PI * 2;
    this.driftPhase = hash(6) * Math.PI * 2;
    this.angle = hash(7) * Math.PI * 2;
    this._animTime = 0;
  }

  /**
   * @param {number} delta
   * @param {number} anchorX
   * @param {number} anchorZ
   */
  update(delta, anchorX, anchorZ) {
    this._animTime += delta;
    this.angle += this.orbitSpeed * delta;

    const wobble =
      Math.sin(this._animTime * 0.55 + this.driftPhase) * this.config.altitudeWobble;
    const x =
      anchorX + Math.cos(this.angle + this.orbitPhase) * this.orbitRadius;
    const z =
      anchorZ + Math.sin(this.angle + this.orbitPhase) * this.orbitRadius;
    const y = this.altitude + wobble;

    this.root.position.set(x, y, z);

    const yaw = this.angle + this.orbitPhase + Math.PI / 2;
    this.root.rotation.set(
      Math.sin(this._animTime * 0.65 + this.bankPhase) * this.config.bankAmplitude,
      yaw,
      Math.cos(this._animTime * 0.48 + this.bankPhase) * this.config.bankAmplitude * 0.7,
    );
  }

  dispose() {
    this.root.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
      }
    });
    this.root.clear();
  }
}
